import type { FieldValues, Path } from 'react-hook-form';
import { Controller, useFormContext, useFormState } from 'react-hook-form';

import Dropdown, { type DropdownProps } from '../Dropdown/Dropdown';

type PresetProps =
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'name'
  | 'error'
  | 'helperText';

export type RhfDropdownProps<T extends FieldValues> = Omit<
  DropdownProps,
  PresetProps
> & {
  name: Path<T>;
  helperText?: DropdownProps['helperText'];
  onValueChange?: (value: string) => void;
};

export default function RhfDropdown<T extends FieldValues>({
  name,
  helperText,
  onValueChange,
  ...rest
}: RhfDropdownProps<T>) {
  const { control, trigger } = useFormContext<T>();
  const { errors } = useFormState({ control, name });
  const fieldError = errors[name];
  const errorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;
  const invalid = Boolean(fieldError);

  return (
    <Controller<T>
      name={name}
      control={control}
      render={({ field }) => (
        <Dropdown
          {...rest}
          name={field.name}
          value={typeof field.value === 'string' ? field.value : ''}
          onChange={(value) => {
            field.onChange(value);
            onValueChange?.(value);
            void trigger(name);
          }}
          onBlur={() => {
            field.onBlur();
            void trigger(name);
          }}
          error={invalid}
          helperText={invalid ? errorMessage : helperText}
        />
      )}
    />
  );
}
